import React, { Fragment, useState, useCallback, useRef, useMemo } from 'react'
import { Col, Container, Row, Button } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { HashLink } from 'react-router-hash-link'
import { useQueries } from 'react-query'
import { IconContext } from 'react-icons'
import { FaCircle, FaChevronLeft, FaChevronRight } from 'react-icons/fa'
import { GiCircle, GiPlainCircle } from 'react-icons/gi'
import styled, { keyframes } from 'styled-components'
import Slider from 'react-slick'
import Embed from 'components/Embed'
import Spinner from 'components/Spinner'
import Map from 'components/Map'
import ResponsiveImage from 'components/ResponsiveImage'
import SanitizedHtml from 'components/SanitizedHtml'
import { IconElement } from 'components/Icons'
import { fetchPosts, fetchHverfi, fetchSliders, fetchMap } from 'api'

import 'slick-carousel/slick/slick.css'

const SliderCard = styled.div`
  padding: 20px 30px;
  background: #153d5c;
`

const HeartBeatAnimation = keyframes`
  0% {
    transform: scale(1);
  }

  14% {
    transform: scale(1.3);
  }

  28% {
    transform: scale(1);
  }

  42% {
    transform: scale(1.3);
  }

  70% {
    transform: scale(1);
  }
`

const OverlayBtnContainer = styled.div`
  padding: 0 30px 25px 30px;

  @media (max-width: 991px) {
    background: rgba(21, 61, 92, 1);

    button {
      border-color: #e2e2e2 !important;
      background: transparent !important;

      &:hover {
        color: #212529 !important;
        background-color: #e2e2e2 !important;
      }
    }

    span {
      font-size: 1.3rem;
    }
  }

  @media (min-width: 992px) {
    padding: 0;
    position: absolute;
    display: flex;
    align-items: flex-end;
    top: 0;
    right: 25px;
    height: 450px;

    .btn {
      animation: ${HeartBeatAnimation} 1.6s ease-in 2;
      animation-delay: 3s;
    }

    span {
      font-size: 1.8rem;
    }
  }

  @media (min-width: 1200px) {
    height: 500px;
  }

  @media (min-width: 1400px) {
    height: 65%;
  }
`

const SliderOverlay = styled.div`
  @media (min-width: 992px) {
    display: flex;
    align-items: center;
    position: absolute;
    top: 0;
    left: 30px;
    bottom: 0;
    width: 360px;

    ${SliderCard} {
      padding: 30px;
      background: rgba(21, 61, 92, 0.7);
    }
  }
`

const TriangleWrap = styled.div`
  position: relative;
`

const StyledTriangle = styled.svg`
  width: 100%;
  position: absolute;
  bottom: 0;
  right: 0;

  path {
    opacity: 0.9;
  }
`

const TriangleContentWrap = styled.div`
  position: absolute;
  bottom: 2rem;
  right: 3rem;
  left: 3rem;
  display: flex;
  align-items: center;
  justify-content: end;
`

const TriangleContent = styled.div``

const TriangleHeading = styled.div`
  svg {
    width: 20rem;
    height: auto;
    margin-bottom: 1rem;
  }
`

const StyledDots = styled.div`
  position: absolute;
  bottom: 1rem;
  z-index: 1;
  left: 50%;
  margin-left: -5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;

  @media (min-width: 991px) {
    bottom: 2rem;
  }

  svg {
    fill: white;
    width: 1.25rem !important;
    height: 1.25rem !important;
    display: block;
  }
`

const SloganStyles = styled.div`
  position: absolute;
  left: 3rem;
  top: 2.5rem;

  svg {
    width: 20rem;

    @media (min-width: 1024px) {
      width: 26rem;
    }

    @media (min-width: 1600px) {
      width: 38rem;
    }
  }
`

const Slogan = (props) => (
  <SloganStyles>
    <svg viewBox="0 0 336.8 178.6" {...props}>
      <path
        fill="#fff"
        d="M47.8 16.1C42 11 34.5 10.4 29.4 10.4h-7.6V1H5.1v69h16.7V55.6h8.9c5 0 12.4-.6 17.9-6.2 2.7-2.7 6-7.8 6-16.7 0-7-2.3-12.6-6.8-16.6zm-26 8.1h4.6c4.1 0 6.9.8 8.6 2.4 1.5 1.4 2.4 3.7 2.4 6.3 0 2.5-.9 4.9-2.4 6.4-2.3 2.2-5.8 2.4-8.3 2.4h-4.9V24.2zM126.1 70 98.9 1H83.8L56 70h17.4l5.2-12.9h25l5.1 12.9h17.4zM99 44.1H83.6l7.7-20.9L99 44.1zM135.3 70h22.5c11.2 0 20.4-3.5 27.2-10.3 6.3-6.3 9.4-14.5 9.4-24.4 0-10-3.3-18.4-9.9-24.7-8.7-8.5-18.6-9.6-27.2-9.6h-22v25.9h-9.2v12.2h9.2V70zM152 26.9V15.3h3.7c4.5 0 11.1.6 16.2 5.7 2.5 2.5 5.5 7 5.5 14.3 0 6.7-2.4 12.7-6.5 16-3.5 3-8.6 4.5-15.2 4.5H152V39.1h12.2V26.9H152zm116.4 28.9h-22.8V41.7h21.7V27.4h-21.7V15.3h22.8V1h-39.5v69h39.5zM313.8 42c3.3-.9 6.5-2.8 9-5.1 3.7-3.6 5.5-8.2 5.5-14.2 0-8.4-3.8-13.4-6.1-15.6-2.9-2.8-8.4-6.1-17.7-6.1h-24.4v69h16.7V44.3L314.4 70h20.4l-21-28zm-17-27.8h3.2c3.7 0 6.6.7 8.4 2 2 1.5 3 3.7 3 6.5 0 3.4-1.5 5.4-2.4 6.3-2.5 2.5-6.1 2.8-9 2.8h-3.1V14.2zM36.3 136l-5.1-1.9c-.9-.4-1.8-.7-2.6-.9-2.9-1-4.6-1.7-5.8-2.8-.9-.8-1.4-1.9-1.4-3.4s.6-2.9 1.6-3.9c1.7-1.9 4.3-2.2 6.3-2.2 3.8 0 7.6 1.6 11.1 4.8l.4.4 7.6-12.3-.4-.3c-6.2-4.6-12.9-6.8-20.5-6.8s-12.9 2-17.2 6.4c-4.1 4.2-6.1 9.3-6.1 15.6 0 5.2 1.4 9.2 4.2 12.2 2.7 2.9 6.4 4.4 9.7 5.5l5.7 2c1.9.7 6 2.2 7.7 3.7 1.5 1.3 2.2 2.7 2.2 4.5 0 2.8-1.4 4.4-2.3 5.1-2.1 1.8-4.9 2.2-6.8 2.2-3 0-5.7-.8-8.3-2.4-1.8-1.1-4.6-3.5-6.2-5.1l-.4-.4-8.8 12 .3.3c2.3 2.3 6.3 5.6 10.1 7.5 4 2 8.2 2.9 13.7 2.9 8.6 0 15.2-2.4 19.6-7.1 3.9-4 6.2-10.4 6.2-17.2 0-3.7-.9-9-5.2-13-2.4-2.6-5.9-4.1-9.3-5.4zm18.3-13.7h14.8V177h16.7v-54.7h14.8V108H54.6zm95.4 26.1c0 3.5-.4 7.9-3.4 11.1-2.1 2.3-5.2 3.4-9.1 3.4s-7-1.1-9.1-3.4c-3-3.1-3.4-7.6-3.4-11.1V108h-16.7v40.8c0 6.9.9 14.9 7.7 21.9 6.8 7 14.7 7.9 21.6 7.9 6.9 0 14.8-.9 21.6-7.9 6.8-7 7.7-15 7.7-21.9V108H150v40.4zm23.9-26.1h14.8V177h16.7v-54.7h14.8V108h-46.3zm52.3 0H241V177h16.7v-54.7h14.8V108h-46.3z"
      />
    </svg>
  </SloganStyles>
)

const Triangle = ({ fill = '#005a9c', ...props }) => (
  <StyledTriangle viewBox="0 0 1739.44 295.73">
    <path fill={fill} d="M0 295.73h1739.44V0L0 295.73z" />
  </StyledTriangle>
)

const HeroBanner = styled.div`
  position: relative;
  padding-bottom: 47.8125%;
  overflow: hidden;

  > svg {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: auto;

    @media (max-width: 768px) {
      display: none;
    }
  }

  .btn span {
    font-size: 6.5rem;

    @media (min-width: 991px) {
      font-size: 6rem;
    }

    @media (min-width: 1200px) {
      font-size: 5.5rem;
    }
  }
`

const SliderContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: auto;
`

const ThadErStutt = () => {
  const imageSlider = useRef(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  const slickSettings = useMemo(() => ({
    beforeChange: (_, next) => setCurrentSlide(next),
    arrows: false,
    autoplay: true,
    autoplaySpeed: 7000,
    fade: true,
  }))

  const slides = [
    { id: 3, file: require('images/thad-er-stutt/3M6A5615.jpg') },
    { id: 1, file: require('images/thad-er-stutt/3M6A4698.jpg') },
    { id: 2, file: require('images/thad-er-stutt/3M6A5175.jpg') },
  ]

  const goToSlide = (idx) => {
    if (imageSlider.current) {
      imageSlider.current?.slickGoTo(idx)
    }
  }

  return (
    <div className="position-relative mb-2">
      <HeroBanner>
        <SliderContainer>
          <Slider {...slickSettings} ref={imageSlider}>
            {slides.map(({ id, file }) => (file ? <img src={file} /> : null))}
          </Slider>
        </SliderContainer>

        <svg viewBox="0 0 3840 1836">
          <path fill="#005b9d" opacity={0.85} d="M0 1836h3840v-652.9L0 1836z" />
          <path
            fill="#fff"
            d="M2932.3 1555.5h-61.8l-16 35.1h-24l72.4-153.8 68.9 153.8h-24l-15.5-35.1zm-8.8-20.9-21.8-49.3-22.2 49.3h44zm88.8-31.5 56-57.8h29.3l-65.8 65.8 65.8 79.6h-29.8l-51.6-64.4-4.4 4.4v60h-22.2v-145.3h22.2v57.8h.5zm131.2-57.8c17.8 0 28.9 2.2 38.2 8.4 16 10.7 17.3 28 17.3 34.2 0 20.9-12.9 36-32.4 40.4l45.3 62.2h-26.7l-41.8-59.6h-4v59.6h-22.2v-145.3c0 .1 26.3.1 26.3.1zm-3.6 66.6h7.1c6.2 0 31.1-.4 31.1-23.6 0-20.4-19.6-22.2-30.2-22.2h-7.6v45.8h-.4zm179.6 43.6h-62.2l-16 35.1h-24l72.4-153.8 68.9 153.8h-24l-15.1-35.1zm-9.4-20.9-21.8-49.3-22.2 49.3h44zm66.7 56v-155.1l105.3 110.2v-100.4h22.2v154.2L3399 1489.3v101.3h-22.2zm242.7-124.4h-57.8v36.9h56v20.9h-56v45.8h57.8v20.9h-80v-145.3h80v20.8zm91.5 11.5c-1.3-3.1-3.1-6.2-6.7-9.3-4.4-3.6-9.3-5.3-15.6-5.3-13.3 0-20.4 8-20.4 17.3 0 4.4 1.3 12 15.6 17.8l14.7 5.8c26.7 10.7 34.2 26.2 34.2 42.7 0 27.6-19.6 46.7-46.7 46.7-16.9 0-26.7-6.2-34.2-14.7-8-8.9-11.1-18.2-12-28.4l21.8-4.9c0 7.1 2.7 13.8 6.2 18.7 4 5.3 10.2 8.9 19.1 8.9 13.3 0 24-9.8 24-24.4 0-14.7-11.1-20.9-20.9-24.9l-13.8-5.8c-12-4.9-29.8-15.1-29.8-36.9 0-19.6 15.1-38.2 42.7-38.2 15.6 0 24.4 5.8 29.3 9.8 4 3.6 8 8.4 11.1 14.7l-18.6 10.4zM446.6 243.3c-24.3-21.4-55.8-23.9-77.1-23.9h-31.9V180h-70v289.3h70v-60.4h37.3c21 0 52-2.5 75.1-26 11.3-11.3 25.2-32.7 25.2-70 0-29.3-9.7-52.8-28.6-69.6zm-109 34h19.3c17.2 0 28.9 3.4 36.1 10.1 6.3 5.9 10.1 15.5 10.1 26.4 0 10.5-3.8 20.5-10.1 26.8-9.6 9.2-24.3 10.1-34.8 10.1h-20.5c-.1-.1-.1-73.4-.1-73.4zm437.3 192L660.9 180h-63.3L481 469.3h73l21.8-54.1h104.8l21.4 54.1h72.9zM661.3 360.7h-64.6l32.3-87.6 32.3 87.6zm152.2 108.6h94.3c47 0 85.5-14.7 114-43.2 26.4-26.4 39.4-60.8 39.4-102.3 0-41.9-13.8-77.1-41.5-103.6-36.5-35.6-78-40.3-114-40.3h-92.2v108.6h-38.6v51.2h38.6v129.6zm70-180.7V240H899c18.9 0 46.5 2.5 67.9 23.9 10.5 10.5 23.1 29.4 23.1 60 0 28.1-10.1 53.2-27.3 67.1-14.7 12.6-36.1 18.9-63.7 18.9h-15.5v-70h51.2v-51.2h-51.2zm488.1 121.2H1276v-59.1h91v-60h-91V240h95.6v-60H1206v289.3h165.6v-59.5zm190.3-57.9c13.8-3.8 27.3-11.7 37.7-21.4 15.5-15.1 23.1-34.4 23.1-59.5 0-35.2-15.9-56.2-25.6-65.4-12.2-11.7-35.2-25.6-74.2-25.6h-102.3v289.3h70V361.6l73.8 107.8h85.5l-88-117.5zm-71.2-116.6h13.4c15.5 0 27.7 2.9 35.2 8.4 8.4 6.3 12.6 15.5 12.6 27.3 0 14.3-6.3 22.6-10.1 26.4-10.5 10.5-25.6 11.7-37.7 11.7h-13v-73.8h-.4zM398.4 746l-21.4-8c-3.8-1.7-7.5-2.9-10.9-3.8-12.2-4.2-19.3-7.1-24.3-11.7-3.8-3.4-5.9-8-5.9-14.3s2.5-12.2 6.7-16.4c7.1-8 18-9.2 26.4-9.2 15.9 0 31.9 6.7 46.5 20.1l1.7 1.7 31.9-51.6-1.7-1.3c-26-19.3-54.1-28.5-86-28.5s-54.1 8.4-72.1 26.8c-17.2 17.6-25.6 39-25.6 65.4 0 21.8 5.9 38.6 17.6 51.2 11.3 12.2 26.8 18.4 40.7 23.1l24 8.5c8 2.9 25.2 9.2 32.3 15.5 6.3 5.5 9.2 11.3 9.2 18.9 0 11.7-5.9 18.4-9.6 21.4-8.8 7.5-20.5 9.2-28.5 9.2-12.6 0-23.9-3.4-34.8-10.1-7.5-4.6-19.3-14.7-26-21.4l-1.7-1.7-36.9 50.4 1.3 1.3c9.6 9.6 26.4 23.5 42.3 31.4 16.8 8.4 34.4 12.2 57.4 12.2 36.1 0 63.7-10.1 82.2-29.8 16.4-16.8 26-43.6 26-72.1 0-15.5-3.8-37.7-21.8-54.5-10-10.9-24.7-17.2-39-22.7zm76.8-57.4h62.1V918h70V688.6h62.1v-60H475.2v60zm400 109.4c0 14.7-1.7 33.1-14.3 46.5-8.8 9.6-21.8 14.3-38.2 14.3s-29.4-4.6-38.2-14.3c-12.6-13-14.3-31.9-14.3-46.5V628.6h-70v171.1c0 28.9 3.8 62.5 32.3 91.8 28.5 29.4 61.6 33.1 90.6 33.1 28.9 0 62.1-3.8 90.6-33.1 28.5-29.4 32.3-62.9 32.3-91.8V628.6h-70.9V798h.1zm100.2-109.4h62.1V918h70V688.6h62.1v-60H975.4v60zm219.3 0h62.1V918h70V688.6h62.1v-60h-194.1v60z"
          />

          <foreignObject x={2830} y={1627} width={900} height={180}>
            <IconContext.Provider value={{ size: '64px', className: 'd-block' }}>
              <Button
                as={Link}
                to="/thad-er-stutt"
                variant="outline-light"
                size="lg"
                className="px-3 py-2 d-inline-flex align-items-center justify-content-between w-100"
              >
                <span className="pl-3 pr-3">Það er stutt...</span>
                <FaChevronRight />
              </Button>
            </IconContext.Provider>
          </foreignObject>
        </svg>

        <StyledDots>
          {slides.map(({ id }, idx) => {
            const isActive = currentSlide === idx
            const props = {
              role: 'button',
              onClick: () => goToSlide(idx),
              key: idx,
            }

            return isActive ? <GiPlainCircle {...props} /> : <GiCircle {...props} />
          })}
        </StyledDots>
      </HeroBanner>
      <div className="d-flex justify-content-between align-items-center d-md-none bg-primary text-white pt-3 px-4 pb-3 flex-wrap gap-4">
        <svg viewBox="0 0 206.1 39.6" width="180" className="d-block">
          <path
            fill="currentColor"
            d="M23.9 28H10l-3.6 7.9H1L17.3 1.3l15.5 34.6h-5.4L23.9 28zm-2-4.7L17 12.2l-5 11.1h9.9zm20-7.1 12.6-13h6.6L46.3 18l14.8 17.9h-6.7L42.8 21.4l-1 1v13.5h-5V3.2h5v13zm29.5-13c4 0 6.5.5 8.6 1.9 3.6 2.4 3.9 6.3 3.9 7.7 0 4.7-2.9 8.1-7.3 9.1l10.2 14h-6l-9.4-13.4h-.9v13.4h-5V3.2h5.9zm-.8 15h1.6c1.4 0 7-.1 7-5.3 0-4.6-4.4-5-6.8-5h-1.7v10.3zM111 28H97l-3.6 7.9H88l16.3-34.6 15.5 34.6h-5.4L111 28zm-2.1-4.7L104 12.2l-5 11.1h9.9zm15 12.6V1l23.7 24.8V3.2h5v34.7l-23.7-24.8v22.8h-5zm54.6-28h-13v8.3h12.6v4.7h-12.6v10.3h13v4.7h-18V3.2h18v4.7zm20.6 2.6c-.3-.7-.7-1.4-1.5-2.1-1-.8-2.1-1.2-3.5-1.2-3 0-4.6 1.8-4.6 3.9 0 1 .3 2.7 3.5 4l3.3 1.3c6 2.4 7.7 5.9 7.7 9.6 0 6.2-4.4 10.5-10.5 10.5-3.8 0-6-1.4-7.7-3.3-1.8-2-2.5-4.1-2.7-6.4l4.9-1.1c0 1.6.6 3.1 1.4 4.2.9 1.2 2.3 2 4.3 2 3 0 5.4-2.2 5.4-5.5s-2.5-4.7-4.7-5.6l-3.1-1.3c-2.7-1.1-6.7-3.4-6.7-8.3 0-4.4 3.4-8.6 9.6-8.6 3.5 0 5.5 1.3 6.6 2.2.9.8 1.8 1.9 2.5 3.3l-4.2 2.4z"
          />
        </svg>
        <IconContext.Provider value={{ size: '32px', className: 'd-block' }}>
          <Button
            as={Link}
            to="/thad-er-stutt"
            variant="outline-light"
            size="lg"
            className="px-2 py-2 d-inline-flex align-items-center justify-content-between mt-2 mb-2"
          >
            <span className="pl-2 pr-3" style={{ fontSize: '1.25rem' }}>
              Það er stutt...
            </span>
            <FaChevronRight />
          </Button>
        </IconContext.Provider>
      </div>
    </div>
  )
}

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const imageSlider = useRef(null)
  const contentSlider = useRef(null)

  const slickSettings = useMemo(() => ({
    beforeChange: (_, next) => setCurrentSlide(next),
    arrows: false,
  }))

  const [posts, cards, sliders, markers] = useQueries([
    { queryKey: 'posts', queryFn: fetchPosts },
    { queryKey: 'hverfi', queryFn: fetchHverfi },
    { queryKey: 'sliders', queryFn: fetchSliders },
    { queryKey: 'thjonustukort', queryFn: fetchMap },
  ])

  const post = useMemo(() => posts.data?.find?.((i) => i.key === 'frabaer-thjonusta'), [posts.data])

  const slides = useMemo(() => {
    const slider = sliders.data?.find?.((i) => i.key === 'forsida') ?? {}

    return slider.slides?.sort?.((a, b) => a.slider_sort - b.slider_sort) ?? []
  }, [sliders.data])

  const isLoading = posts.isLoading || cards.isLoading || sliders.isLoading || markers.isLoading
  const prevSlide = () => imageSlider.current.slickPrev?.()
  const nextSlide = () => imageSlider.current.slickNext?.()

  return isLoading ? (
    <Spinner />
  ) : (
    <>
      {/* <ThadErStutt /> */}
      {slides.length > 0 ? (
        <div className="position-relative">
          <Slider asNavFor={contentSlider.current} ref={imageSlider} {...slickSettings}>
            {slides.map(({ id, file }) => (file ? <ResponsiveImage key={id} file={file} /> : null))}
          </Slider>
          <SliderOverlay>
            <SliderCard className="w-100 text-white">
              <Slider asNavFor={imageSlider.current} ref={contentSlider} {...slickSettings}>
                {slides.map(({ id, title, body }) => (
                  <Fragment key={id}>
                    <h4 className="mb-1">{title}</h4>
                    <SanitizedHtml html={body} />
                  </Fragment>
                ))}
              </Slider>

              <div className="d-flex align-items-center">
                <h5 className="m-0 mr-4" style={{ minWidth: '45px' }}>
                  {currentSlide + 1} / {slides.length}
                </h5>

                <IconContext.Provider value={{ size: '16px', className: 'd-block' }}>
                  <Button variant="outline-light" className="p-2" onClick={prevSlide}>
                    <FaChevronLeft />
                  </Button>
                  <Button variant="outline-light" className="p-2" onClick={nextSlide}>
                    <FaChevronRight />
                  </Button>
                </IconContext.Provider>
              </div>
            </SliderCard>
          </SliderOverlay>
        </div>
      ) : null}
      <Container className="py-4 py-lg-5 text-center" fluid>
        <Row className="mx-md-n2">
          {cards.data?.map?.((card) =>
            card?.meta?.icon ? (
              <Col key={card.key} className="px-md-2 mb-3 mb-xl-0" md={6} xl={3}>
                <div className="bordered-box px-3 h-100">
                  <HashLink smooth to={card.meta.link} className="d-block text-decoration-none">
                    <IconElement name={card.meta.icon} width="50px" />
                    <h4 className="mt-3 mb-2">{card.title}</h4>
                    <SanitizedHtml html={card.body} />
                  </HashLink>
                </div>
              </Col>
            ) : null
          )}
        </Row>
      </Container>
      {markers.data ? <Map markers={markers.data} /> : null}
      {post ? (
        <Container className="bg-primary text-white" fluid>
          <Row className="align-items-center">
            <Col xl={8} className="p-0">
              {post.file ? <Embed file={post.file} /> : null}
            </Col>
            <Col xl={4} className="py-4 py-xl-3">
              <h2>{post.title}</h2>
              <SanitizedHtml html={post.body} />
              <Link to="/um-akranes" className="btn btn-white">
                Sjá meira
              </Link>
            </Col>
          </Row>
        </Container>
      ) : null}
    </>
  )
}

export default Home
